import React from "react";
import {
    Box, Typography, Button, alpha, useTheme, useMediaQuery,
    Stack, Chip, Grid, IconButton, Tooltip,
    Card, CardMedia, CardContent, CardActions, Skeleton,
} from "@mui/material";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import { IProduct } from "../../vm";
import { ConfirmDialogContext } from "../../common/ConfirmDialogProvider";
import { getProducts, deleteAProduct } from "../../Service/ProductService";
import ProductForm from "./ProductForm";
import toast from "react-hot-toast";
import CustomerDrawer from "../../common/CustomerDrawer";
import { processUrl } from "../../Service/product.service";
import { STATUS_DICT_FOR_PRODUCT } from "../../common/constant";

const HEADER_GRADIENT = "linear-gradient(135deg, #00695c 0%, #00897b 60%, #26a69a 100%)";
const TEAL = "#00897b";
const TEAL_DARK = "#00695c";

export interface ProductMainProps {}

const ProductMain: React.FC<ProductMainProps> = () => {
    const { showConfirmDialog } = React.useContext(ConfirmDialogContext);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [isLoading, setLoading] = React.useState(false as boolean);
    const [products, setProducts] = React.useState([] as IProduct[]);
    const [manageProductDialog, setManageProductDialog] = React.useState({
        isOpen: false,
        data: {},
        editIndex: -1,
    } as { isOpen: boolean; data: any; editIndex: number });

    React.useEffect(() => {
        const asyncFunc = async () => {
            setLoading(true);
            await getAllProduct();
        };
        asyncFunc();
    }, []);

    const getAllProduct = async () => {
        const result = await getProducts();
        setProducts(result.data);
        setLoading(false);
    };

    const handleAddDialogOpen = (isOpen: boolean, editIndex?: number) => {
        let data: any = undefined;
        if (editIndex !== undefined) {
            data = products[editIndex];
        }
        setManageProductDialog({
            isOpen,
            data,
            editIndex: editIndex !== undefined ? editIndex : -1,
        });
    };

    const handleClose = (data?: IProduct) => {
        let productList = [...products];
        if (data) {
            if (manageProductDialog.editIndex !== -1) {
                productList[manageProductDialog.editIndex] = data;
            } else {
                productList.unshift(data);
            }
        }
        setProducts(productList);
        setManageProductDialog({ isOpen: false, data: undefined, editIndex: -1 });
    };

    const deleteProduct = (index: number) => {
        showConfirmDialog("Are you sure", "Do you want to delete?", async () => {
            let productList = [...products];
            setLoading(true);
            let result = await deleteAProduct(productList[index]._id as string);
            setLoading(false);
            if (result?.success) {
                toast.success("Product deleted successfully");
                productList.splice(index, 1);
                setProducts(productList);
            } else {
                toast.error(result?.message || "Error while deleting product");
            }
        });
    };

    return (
        <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
            {/* Page Header */}
            <Box
                sx={{
                    background: HEADER_GRADIENT,
                    px: { xs: 2, sm: 4 },
                    py: { xs: 2.5, sm: 3.5 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    boxShadow: `0 4px 20px ${alpha(TEAL, 0.4)}`,
                }}
            >
                <Stack direction="row" alignItems="center" gap={2}>
                    <Box
                        sx={{
                            bgcolor: alpha("#fff", 0.15),
                            borderRadius: 2,
                            p: 1,
                            display: "flex",
                            alignItems: "center",
                        }}
                    >
                        <Inventory2Icon sx={{ color: "#fff", fontSize: { xs: 28, sm: 36 } }} />
                    </Box>
                    <Box>
                        <Typography
                            variant={isMobile ? "h5" : "h4"}
                            fontWeight={700}
                            sx={{ color: "#fff", lineHeight: 1.2 }}
                        >
                            Products
                        </Typography>
                        <Typography variant="body2" sx={{ color: alpha("#fff", 0.8), mt: 0.3 }}>
                            Manage your product catalog
                        </Typography>
                    </Box>
                </Stack>

                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Chip
                        label={`${products.length} products`}
                        size="small"
                        sx={{
                            bgcolor: alpha("#fff", 0.2),
                            color: "#fff",
                            fontWeight: 600,
                            fontSize: "0.7rem",
                            display: { xs: "none", sm: "flex" },
                        }}
                    />
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleAddDialogOpen(true)}
                        sx={{
                            bgcolor: alpha("#fff", 0.2),
                            color: "#fff",
                            borderRadius: 2,
                            fontWeight: 600,
                            textTransform: "none",
                            border: `1px solid ${alpha("#fff", 0.3)}`,
                            flexShrink: 0,
                            "&:hover": { bgcolor: alpha("#fff", 0.3) },
                        }}
                    >
                        {isMobile ? "Add" : "Add Product"}
                    </Button>
                </Stack>
            </Box>

            {/* Content */}
            <Box sx={{ px: { xs: 1, sm: 3 }, pt: 2, pb: 4 }}>
                {isLoading ? (
                    <Grid container spacing={2}>
                        {Array.from({ length: 8 }).map((_, i) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                                <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 2, mb: 1 }} />
                                <Skeleton width="80%" height={20} sx={{ mb: 0.5 }} />
                                <Skeleton width="50%" height={16} sx={{ mb: 0.5 }} />
                                <Skeleton width="60%" height={16} />
                            </Grid>
                        ))}
                    </Grid>
                ) : products.length === 0 ? (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            py: 12,
                            gap: 2,
                        }}
                    >
                        <ShoppingBagIcon sx={{ fontSize: 64, color: "text.disabled" }} />
                        <Typography variant="h6" color="text.secondary">
                            No products found
                        </Typography>
                        <Typography variant="body2" color="text.disabled">
                            Click "Add Product" to create your first product
                        </Typography>
                    </Box>
                ) : (
                    <Grid container spacing={2}>
                        {products.map((product, productIndex) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={product._id}>
                                <Card
                                    sx={{
                                        borderRadius: 2,
                                        border: `1px solid ${theme.palette.divider}`,
                                        boxShadow: "none",
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                        transition: "all 0.2s",
                                        "&:hover": {
                                            boxShadow: `0 4px 16px ${alpha(TEAL, 0.18)}`,
                                            borderColor: TEAL,
                                        },
                                    }}
                                >
                                    {product.imageUrl ? (
                                        <CardMedia
                                            component="img"
                                            height="180"
                                            image={processUrl(product.imageUrl)}
                                            alt={product.title}
                                            sx={{ objectFit: "cover" }}
                                        />
                                    ) : (
                                        <Box
                                            sx={{
                                                height: 180,
                                                bgcolor: alpha(TEAL, 0.06),
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <ShoppingBagIcon sx={{ fontSize: 56, color: alpha(TEAL, 0.3) }} />
                                        </Box>
                                    )}

                                    <CardContent sx={{ flexGrow: 1, pb: 0.5 }}>
                                        <Typography
                                            variant="subtitle2"
                                            fontWeight={700}
                                            noWrap
                                            title={product.title}
                                            sx={{ mb: 0.25 }}
                                        >
                                            {product.title}
                                        </Typography>

                                        {product.brand && (
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                {product.brand}
                                                {product.manufacturer ? ` · ${product.manufacturer}` : ""}
                                            </Typography>
                                        )}

                                        <Stack direction="row" spacing={0.5} flexWrap="wrap" mt={1} useFlexGap>
                                            {product.mrp ? (
                                                <Chip
                                                    label={`MRP ₹${product.mrp}`}
                                                    size="small"
                                                    sx={{
                                                        fontSize: "0.65rem",
                                                        height: 20,
                                                        bgcolor: alpha(theme.palette.warning.main, 0.1),
                                                        color: "warning.dark",
                                                    }}
                                                />
                                            ) : null}
                                            {product.sellingPrice ? (
                                                <Chip
                                                    label={`₹${product.sellingPrice}`}
                                                    size="small"
                                                    sx={{
                                                        fontSize: "0.65rem",
                                                        height: 20,
                                                        bgcolor: alpha(TEAL, 0.1),
                                                        color: TEAL_DARK,
                                                        fontWeight: 700,
                                                    }}
                                                />
                                            ) : null}
                                        </Stack>

                                        <Stack direction="row" spacing={0.5} flexWrap="wrap" mt={0.75} useFlexGap>
                                            <Chip
                                                label={STATUS_DICT_FOR_PRODUCT[product.status] || "Unknown"}
                                                size="small"
                                                sx={{
                                                    fontSize: "0.65rem",
                                                    height: 20,
                                                    bgcolor: alpha(theme.palette.info.main, 0.1),
                                                    color: "info.dark",
                                                }}
                                            />
                                            {product.deliveryTime ? (
                                                <Chip
                                                    label={`${product.deliveryTime}d delivery`}
                                                    size="small"
                                                    sx={{ fontSize: "0.65rem", height: 20 }}
                                                />
                                            ) : null}
                                        </Stack>

                                        {(product.noOfBurner || product.materialType) && (
                                            <Typography
                                                variant="caption"
                                                color="text.disabled"
                                                display="block"
                                                mt={0.75}
                                            >
                                                {[
                                                    product.noOfBurner && `${product.noOfBurner} burner`,
                                                    product.materialType,
                                                ]
                                                    .filter(Boolean)
                                                    .join(" · ")}
                                            </Typography>
                                        )}
                                    </CardContent>

                                    <CardActions sx={{ px: 1.5, pb: 1.5, pt: 0.5, justifyContent: "flex-end" }}>
                                        <Tooltip title="Edit product">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleAddDialogOpen(true, productIndex)}
                                                sx={{ color: TEAL }}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete product">
                                            <IconButton
                                                size="small"
                                                onClick={() => deleteProduct(productIndex)}
                                                color="error"
                                            >
                                                <DeleteForeverIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </CardActions>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Box>

            {manageProductDialog.isOpen && (
                //@ts-ignore
                <CustomerDrawer
                    title={manageProductDialog?.data?._id ? "Edit Product" : "Add Product"}
                    handleOpen={true}
                    onClose={() => handleClose()}
                >
                    <ProductForm onClose={handleClose} data={manageProductDialog.data} />
                </CustomerDrawer>
            )}
        </Box>
    );
};

export default ProductMain;
